import {
  defaultCostStates,
  defaultCostTypes,
  generateIDFromDate,
  CostLifecycleStatus,
  ICost,
  ICostingList,
  spErrorTags,
  spLogTags,
} from "@eventr/common";
import { DataAPI } from "data/Data";
import { bs } from "state/bound.store";
import i18n from "i18n/i18next";
import { isDistanceExpense, StandardExpenseFormValues, DistanceExpenseFormValues } from "./forms/expense-form-utils";
import { useConnectivityStore } from "feat/connectivity/connectivity.store";
import { expenseLocalStore, LocalExpense } from "./local-expense-store/local-expenses";
import { Expense } from "state/expenses.slice";
import { useLanguageContext } from "state/languageState/languageContext";
import { ColoursObj, Icon } from "@eventr/common-storybook";
import { mapStandardExpense, mapDistanceExpense } from "./expense-mappers";
import { BaseAddExpense, BaseEditExpense } from "./expense-types";

const getEntityId = (itineraryId: number, sectionId: number) => {
  return `${itineraryId}.${sectionId}.000000000000`;
};

export const deleteExpense = async (existingCost: ICost | undefined) => {
  const itineraryId = bs().selectedItinID;
  const entityId = existingCost?.entity_id;

  if (!itineraryId || !entityId) {
    return {
      status: "error",
      message: "Invalid itinerary ID or expense",
    };
  }

  const deletedCost: ICost = {
    _id: existingCost?._id,
    lifecycle_status: "uploading",
    entity_id: existingCost?.entity_id,
    currency: existingCost?.currency,
    amount: existingCost?.amount,
    cost_type: existingCost?.cost_type,
    created_unix: existingCost?.created_unix,
    name: existingCost?.name,
    fieldId: existingCost?.fieldId,
    cost_state: existingCost?.cost_state,
    category: existingCost?.category,
    receipt_file: existingCost?.receipt_file,
    merchant: existingCost?.merchant,
    reimbursable: existingCost?.reimbursable,
    expense: existingCost?.expense,
    users: existingCost?.users,
    edited_by: existingCost?.edited_by,
    invoice_date: existingCost?.invoice_date,
    recieved_date: existingCost?.recieved_date,
    paid_date: existingCost?.paid_date,
    cost_center: existingCost?.cost_center,
    props: existingCost?.props,
    receipt_ai_data: existingCost?.receipt_ai_data,
    expense_type: existingCost?.expense_type,
    distance_info: existingCost?.distance_info,

    deleted: true,
    modified_unix: new Date().getTime(),
  };

  const deleteCostingList: ICostingList = {
    [entityId]: {
      costs: [deletedCost],
    },
  };

  const isOffline = !useConnectivityStore.getState().isConnectivityAvailable;

  if (isOffline) {
    cacheExpense({ cost: { ...deletedCost, lifecycle_status: "local" }, itineraryId, syncAction: "delete" });
    spLogTags(`[delete] Unable to edit expense as we are offline`, ["expenses"]);
    return;
  }

  //cache local expense before attempting delete
  await cacheExpense({ cost: deletedCost, itineraryId, syncAction: "delete" });
  spLogTags(`[delete] Expense ${deletedCost._id} delete saved to local-expense-store, attempting delete now`, [
    "expenses",
  ]);

  try {
    spLogTags(`[delete] Attempting to delete expense ${deletedCost._id}`, ["expenses"]);
    const response = await DataAPI.expenses.deleteExpenses(itineraryId, deleteCostingList);

    if (response.status === "error") throw new Error(`Error deleting expense - ${response?.message}`);

    spLogTags(`[delete] Expenses ${deletedCost._id} deleted successfully removing from local-expense-store`, [
      "expenses",
    ]);

    const costFromResponse = Object.values(response.data ?? {})
      .flatMap((costing) => costing.costs)
      .find((cost) => cost._id === deletedCost._id);

    if (!costFromResponse) throw new Error(`Unable to find cost in delete response`);

    expenseLocalStore.remove(deletedCost._id);
    bs().upsertExpense({ cost: costFromResponse }, itineraryId);
  } catch (err) {
    cacheExpense({ cost: { ...deletedCost, lifecycle_status: "local" }, itineraryId, syncAction: "delete" });
    spLogTags(`[delete] Failed to delete ${deletedCost._id}. Error: ${err}`, ["expenses"]);
  }
};

export const editExpense = async (
  formData: StandardExpenseFormValues | DistanceExpenseFormValues,
  existingCost: ICost | undefined,
) => {
  const itineraryId = bs().selectedItinID;
  const entityId = existingCost?.entity_id;

  if (!itineraryId || !entityId) {
    return {
      status: "error",
      message: "Invalid Itinerary ID or expense",
    };
  }

  const baseEditExpense: BaseEditExpense = {
    //all fields carried across on edit
    _id: existingCost?._id ?? "",
    lifecycle_status: "uploading",
    entity_id: existingCost?.entity_id,
    cost_type: existingCost?.cost_type,
    created_unix: existingCost?.created_unix,
    fieldId: existingCost?.fieldId,
    cost_state: existingCost?.cost_state,
    reimbursable: existingCost?.reimbursable,
    deleted: existingCost?.deleted,
    expense: existingCost?.expense,
    users: existingCost?.users,
    recieved_date: existingCost?.recieved_date,
    paid_date: existingCost?.paid_date,
    props: existingCost?.props,
    receipt_ai_data: existingCost?.receipt_ai_data,
    //old receipt_url has been removed
    receipt_file: isDistanceExpense(formData) ? undefined : !formData.fileData ? undefined : existingCost.receipt_file,
    modified_unix: new Date().getTime(),
    edited_by: bs().wpUser?.id.toLocaleString(),
  };

  const newCost: ICost = {
    ...baseEditExpense,
    ...(isDistanceExpense(formData) ? mapDistanceExpense(formData) : mapStandardExpense(formData)),
  };

  const editCostingList: ICostingList = {
    [entityId]: {
      costs: [newCost],
    },
  };

  //if we have added the expense locally, make sure if we edit it before uploading its still an add not an edit
  const localExpenseInManager = (await expenseLocalStore.getAll()).find((e) => e.data.cost._id === existingCost._id);
  const localSynAction = localExpenseInManager?.data.syncAction;
  const syncAction = localSynAction === "add" ? "add" : "edit";

  const isOffline = !useConnectivityStore.getState().isConnectivityAvailable;

  if (isOffline) {
    spLogTags(`[edit] Unable to edit expense as we are offline`, ["expenses"]);
    cacheExpense({ cost: { ...newCost, lifecycle_status: "local" }, itineraryId, syncAction });
    return;
  }

  //cache expense before attempting to edit it
  // const file = formData.file;
  await cacheExpense({ cost: newCost, itineraryId, syncAction });

  spLogTags(`[edit] Expense ${newCost._id} edit saved to local-expense-store, attempting edit now`, ["expenses"]);

  try {
    spLogTags(`[edit] Attempting to edit ${newCost._id}`, ["expenses"]);
    const response = await DataAPI.expenses.editExpenses(itineraryId, editCostingList);

    if (response.status === "error") throw new Error(`Error editing expense - ${response?.message}`);

    const costFromResponse = Object.values(response.data ?? {})
      .flatMap((costing) => costing.costs)
      .find((cost) => cost._id === newCost._id);

    if (!costFromResponse) throw new Error(`Unable to find cost in edit response`);

    spLogTags(`[edit] Edit successful ${newCost._id} removing from local-expense-store`, ["expenses"]);
    expenseLocalStore.remove(newCost._id);
    bs().upsertExpense({ cost: costFromResponse }, itineraryId);
  } catch (err) {
    cacheExpense({ cost: { ...newCost, lifecycle_status: "local" }, itineraryId, syncAction });
    spLogTags(`[edit] Failed to edit ${newCost._id}, saved to local store. Error: ${err}`, ["expenses"]);
  }
};

export const addExpense = async (formData: StandardExpenseFormValues | DistanceExpenseFormValues) => {
  const itineraryId = bs().selectedItinID;
  //section on the form is used to create the entityId when first added
  const entityId = getEntityId(itineraryId, formData.section);

  if (!itineraryId || !entityId) {
    return { status: "error", message: "Invalid itinerary ID or expense" };
  }

  const baseAddExpense: BaseAddExpense = {
    _id: generateIDFromDate(),
    lifecycle_status: "uploading",
    entity_id: entityId,
    users: [bs().wpUser?.id ?? 0],
    expense: true,
    created_unix: new Date().getTime(),
    modified_unix: new Date().getTime(),
    edited_by: bs().wpUser?.id.toLocaleString(),
    cost_type: defaultCostTypes.Payment,
    cost_state: defaultCostStates.Pending,
  };

  const newCost: ICost = {
    ...baseAddExpense,
    ...(isDistanceExpense(formData) ? mapDistanceExpense(formData) : mapStandardExpense(formData)),
  };

  const newCostingList: ICostingList = {
    [entityId]: {
      costs: [newCost],
    },
  };

  const isOffline = !useConnectivityStore.getState().isConnectivityAvailable;

  if (isOffline) {
    cacheExpense({ cost: { ...newCost, lifecycle_status: "local" }, itineraryId, syncAction: "add" });
    spLogTags(`[add] Unable to add expense as we are offline`, ["expenses"]);
    return;
  }

  //cache expense before we attempt adding it
  await cacheExpense({ cost: newCost, itineraryId, syncAction: "add" });
  spLogTags(`[add] Expense ${newCost._id} add saved to local-expense-store, attempting add now`, ["expenses"]);

  try {
    spLogTags(`[add] Attempting to add ${newCost._id}`, ["expenses"]);
    const response = await DataAPI.expenses.addExpenses(itineraryId, newCostingList);

    if (response.status === "error") throw new Error(`Error adding expense - ${response?.message}`);

    const costFromResponse = Object.values(response.data ?? {})
      .flatMap((costing) => costing.costs)
      .find((cost) => cost._id === newCost._id);

    if (!costFromResponse) throw new Error(`Unable to find cost in add response`);
    spLogTags(`[add] Add successful ${newCost._id} , removing from local-expense-store and updating remote expenses`, [
      "expenses",
    ]);
    expenseLocalStore.remove(newCost._id);
    //get result from backend
    bs().upsertExpense({ cost: costFromResponse }, itineraryId);
  } catch (err) {
    cacheExpense({ cost: { ...newCost, lifecycle_status: "local" }, itineraryId, syncAction: "add" });
    spLogTags(`[add] Failed to add ${newCost._id}, saved to local store. Error: ${err}`, ["expenses"]);
  }
};

export const getModifiedTime = (e?: ICost) => (e?.modified_unix ? new Date(e?.modified_unix).getTime() : 0);

/**
 * Lifecycle statuses that mean the server still owes us an AI receipt result.
 *
 * WordPress hands a newly saved receipt to bookingsai and flips the cost to
 * `receipt-processing`; the gateway writes `receipt-processed` plus `receipt_ai_data` when the
 * answer comes back. `uploading` is included because the add/edit response is built before the
 * hand-off runs, so the cost the app gets back still carries the status it was sent with.
 */
const AWAITING_AI_STATUSES: CostLifecycleStatus[] = ["uploading", "receipt-processing"];

/** Whether this expense has a receipt that the AI has not returned a result for yet. */
export const isAwaitingAiReceipt = (expense: Expense) => {
  const hasReceipt = Boolean(expense.cost.receipt_file ?? expense.cost.receipt_url);
  const status = expense.cost.lifecycle_status;
  return hasReceipt && status !== undefined && AWAITING_AI_STATUSES.includes(status);
};

export type CostDisplayState = "local" | "uploading" | "rejected" | "pending" | "approved";

export const getExpenseDisplayState = (expense: Expense): CostDisplayState => {
  if (expense.cost.lifecycle_status === "local") return "local";
  if (expense.cost.lifecycle_status === "uploading") return "uploading";

  switch (expense.cost.cost_state) {
    case defaultCostStates.Cancelled:
      return "rejected";
    case defaultCostStates.Paid:
      return "approved";
    case defaultCostStates.Overdue:
    case defaultCostStates.Pending:
    case defaultCostStates.Received:
    default:
      return "pending";
  }
};

export const getStateIcon = (state: CostDisplayState, size: string) => {
  switch (state) {
    case "approved":
      return <Icon name="Okay" height={size} width={size} iconColour={ColoursObj.states.approved} />;
    case "rejected":
      return <Icon name="Cancel" height={size} width={size} iconColour={ColoursObj.states.rejected} />;
    case "local":
      return <Icon name="Important" height={size} width={size} iconColour={ColoursObj.states.rejected} />;
    case "pending":
    default:
      return <Icon name="Time" height={size} width={size} iconColour={ColoursObj.states.pending} />;
  }
};

export const getStateColor = (state: CostDisplayState) => {
  switch (state) {
    case "approved":
      return { background: ColoursObj.states.approvedBackground, outline: ColoursObj.states.approved };
    case "rejected":
    case "local":
      return { background: ColoursObj.states.rejectedBackground, outline: ColoursObj.states.rejected };
    case "pending":
    default:
      return { background: ColoursObj.states.pendingBackground, outline: ColoursObj.states.pending };
  }
};

export const getCostStateTranslation = (costState: CostDisplayState) => {
  switch (costState) {
    case "local":
      return i18n.t("cost.status.local");
    case "approved":
      return i18n.t("cost.status.approved");
    case "rejected":
      return i18n.t("cost.status.rejected");
    case "uploading":
      return i18n.t("cost.status.uploading");
    case "pending":
    default:
      return i18n.t("cost.status.pending");
  }
};

export const formatAmountWithCurrency = (amount: number, currency: string | undefined) => {
  if (!currency) return "";
  const locale = useLanguageContext.getState().getLanguageISO639Code();
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    spErrorTags(`[formatAmount] Failed to format currency ${currency}`, ["expenses"], error);
  }
};

export function formatAmount(amount: string | number): string {
  const amountAsNumber = Number(amount);
  return amountAsNumber % 1 === 0 ? amountAsNumber.toString() : amountAsNumber.toFixed(2);
}

export function validateString(v: string | null | undefined) {
  return typeof v === "string" && v.trim().length > 0;
}

export async function cacheExpense(expense: LocalExpense) {
  //save it to the local-expense-store
  await expenseLocalStore.save({ ...expense, org: expense.org ?? bs().selectedOrg?.name });

  //cache it in the expenses.slice which the UI uses to display expenses
  bs().upsertExpense({ cost: expense.cost });
}
